import { useContext, useMemo } from 'react';
import { CustomHomepageIdContext } from './custom-homepage-id-context';
import { CustomHomepageModuleContext } from './custom-homepage-module-context';

/**
 * Merges the Custom Homepage attribution the surrounding providers carry into
 * the caller's analytics traits. Off the homepage neither provider exists, so
 * the traits come back untouched.
 *
 * Memoized on the caller's traits, so it survives a consumer's own re-renders:
 * ContentFrame keeps it in an effect dependency array and emits a video-play
 * event from that effect's cleanup. A caller that rebuilds its traits object
 * each render still produces a new result, which is why a feed card holds its
 * identity while its section re-rendering does not.
 */
export const useCustomHomepageTraits = (
  analyticsData: IAnalyticsData
): IAnalyticsData => {
  const homepageId = useContext(CustomHomepageIdContext);
  const moduleId = useContext(CustomHomepageModuleContext);

  return useMemo(
    () => ({
      ...analyticsData,
      ...(homepageId ? { homepage_id: homepageId } : {}),
      ...(moduleId ? { module_id: moduleId } : {}),
    }),
    [analyticsData, homepageId, moduleId]
  );
};
